import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Button as MuiButton, Stack, Typography } from '@mui/material';
import { BooleanInput, required, SelectInput, TextInput } from 'react-admin';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { useMissionTypes } from '@app/mission_type/useMissionType';
import { StudyFormInput } from '@domain/studyForm';
import { QuestionInputs, getDefaultQuestion } from './QuestionInputs';
import { showModeReadOnlySx } from '@app/root/theme';

const RESERVED_questionIdS = ['count', 'mascot-hug-count'] as const;

export const StudyFormInputs = ({
    readOnly = false,
}: {
    readOnly?: boolean;
}) => {
    const { control } = useFormContext<StudyFormInput>();
    const { memberTypeLabels } = useConfigurationContext();
    const { data: missionTypes = [] } = useMissionTypes();
    const { fields, append, move, remove } = useFieldArray({
        control,
        name: 'formDefinition.questions',
        keyName: 'key',
    });

    return (
        <Stack gap={1} p={1} sx={readOnly ? showModeReadOnlySx : undefined}>
            <TextInput
                source="title"
                label="Title"
                validate={required()}
                fullWidth
                readOnly={readOnly}
            />
            <SelectInput
                source="missionTypeId"
                label="Mission type"
                choices={missionTypes}
                optionText="name"
                optionValue="id"
                validate={required()}
                fullWidth
                readOnly={readOnly}
            />
            <SelectInput
                source="whoCanFill"
                label="Who can fill"
                choices={memberTypeLabels.map(type => ({
                    id: type.value,
                    name: type.label,
                }))}
                validate={required()}
                defaultValue="VOLUNTEER"
                fullWidth
                helperText="Who can fill this form (Volunteer = any member, Main Volunteer = main volunteers and staff, Staff = WWF employees only)"
                readOnly={readOnly}
            />
            <BooleanInput
                source="enabled"
                label="Enabled"
                defaultValue={true}
                readOnly={readOnly}
            />

            <BooleanInput
                source="formDefinition.withMascotHug"
                label="With mascot hug"
                defaultValue={false}
                helperText="Enable the counter of mascot hugs in the form. It will be available only with the mascot animation."
                readOnly={readOnly}
            />
            <Stack gap={1}>
                <Stack gap={0.5} sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">Questions</Typography>
                    <Typography variant="body2" color="text.secondary">
                        The form always begins with a selector for the number of
                        people in the group who have been in contact with the
                        volunteers.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Each question includes the following fields:
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ m: 0, pl: 2.5 }}
                    >
                        <li>
                            Question ID: Enter a unique identifier (preferably
                            human-readable).
                        </li>
                        <li>
                            Question Title: The text displayed to the volunteer.
                        </li>
                        <li>
                            Type: Defines the format of the answer. Options
                            include:
                        </li>
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ m: 0, pl: 5 }}
                    >
                        <li>
                            TWO_CHOICES: A counter to separate between two
                            options (e.g., number of adults and children in the
                            contacted group). Required Choice 1 and Choice 2.
                        </li>
                        <li>
                            YES_NO: A yes/no toggle applied to the entire group.
                            Optional: Sub-question for yes/no toggle.
                        </li>
                        <li>
                            IMAGE_GALLERY: A gallery of images, each with: A
                            unique image key (required). An image value
                            (required). Images are resized and embedded in the
                            form. Volunteers can select one or multiple images
                            that best represent the group (e.g.,
                            nationality/ies).
                        </li>
                    </Typography>
                </Stack>
                {fields.map((field, index) => (
                    <QuestionInputs
                        key={field.key}
                        questionIndex={index}
                        reservedQuestionIds={RESERVED_questionIdS}
                        readOnly={readOnly}
                        onMoveUp={() => move(index, index - 1)}
                        onMoveDown={() => move(index, index + 1)}
                        canMoveUp={index > 0}
                        canMoveDown={index < fields.length - 1}
                        onRemove={() => remove(index)}
                    />
                ))}
                {!readOnly && (
                    <Box>
                        <MuiButton
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => append(getDefaultQuestion('YES_NO'))}
                        >
                            Add question
                        </MuiButton>
                    </Box>
                )}
            </Stack>
        </Stack>
    );
};
